import { Box, BoxProps, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ClickableLinkWithIcon from '../Text/ClickableLinkWithIcon';
import MenuItemWrapper from '../Dropdown/DropdownStyle';
import DownloadIcon from '../Icons/DownloadIcon';
import MenuWrapper from '../Menu/MenuStyle';
import { FileFormat } from '../../models/ImportModels';
import useModal from '../../hooks/useModal/useModal';

interface BoxWrapperProps extends BoxProps {
  disabled: boolean;
}

const BoxWrapper = styled(Box)<BoxWrapperProps>(({ disabled }) => ({
  marginLeft: 'auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '0.375rem',
  flexShrink: 0,
  ':hover': { cursor: 'pointer' },
  ...(disabled && {
    a: {
      opacity: '0.38',
    },
    'svg > path': {
      opacity: '0.38',
    },
    pointerEvents: 'none',
  }),
}));

interface DownloadMenuProps {
  exportMaxNumberOfRows: number;
  downloadExcel: () => void;
  downloadCSV: () => void;
  disabled?: boolean;
  itemLength: number;
}

function DownloadMenu({
  exportMaxNumberOfRows,
  downloadExcel,
  downloadCSV,
  disabled = false,
  itemLength,
}: DownloadMenuProps) {
  const theme = useTheme();
  const [t] = useTranslation();
  const modal = useModal();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handler for triggering download according to specified format
   */
  function download(format: FileFormat) {
    switch (format) {
      case 'csv':
        return downloadCSV();
      case 'xlsx':
        return downloadExcel();
      default:
        return null;
    }
  }

  /**
   * Displays the confirmation modal
   */
  const showConfirmationModal = (format: FileFormat) => {
    modal.showModal({
      modalTitle: t('pages.bulkImport.errorsTitle'),
      modalDescription: t('common.downloadWarning', { exportMaxNumberOfRows: exportMaxNumberOfRows.toString() }),
      confirmationModal: true,
      confirmationButtonLabel: t('common.proceed'),
      onAccept: (setOpen) => {
        setOpen(false);
        download(format);
        handleClose();
      },
      onDiscard: () => {
        handleClose();
      },
    });
  };

  /**
   * Trigger the download flow, showing the warning modal if necessary
   */
  function triggerDownload(format: FileFormat) {
    // Show warning modal only if there are more records to export than exportMaxNumberOfRows
    if (itemLength > exportMaxNumberOfRows) {
      showConfirmationModal(format);
    } else {
      handleClose();
      download(format);
    }
  }

  return (
    <Box>
      <BoxWrapper data-testid="download-menu-trigger" id="box-download-menu" onClick={handleClick} disabled={disabled}>
        <ClickableLinkWithIcon
          sx={{ color: theme.palette.text.primary }}
          onClick={() => {}}
          leftIcon={<DownloadIcon style={{ flexShrink: 0 }} />}
        >
          {t('common.download')}
        </ClickableLinkWithIcon>
      </BoxWrapper>

      <MenuWrapper
        id="menu-download-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root': {
            marginTop: '0.5rem',
            minWidth: '15.5rem !important',
          },
        }}
      >
        <MenuItemWrapper
          onClick={() => {
            triggerDownload('csv');
          }}
        >
          {t('common.downloadMenu.csv')}
        </MenuItemWrapper>
        <MenuItemWrapper
          onClick={() => {
            triggerDownload('xlsx');
          }}
        >
          {t('common.downloadMenu.excel')}
        </MenuItemWrapper>
      </MenuWrapper>
    </Box>
  );
}

export default DownloadMenu;
